import { PrivacyComponent } from './components/public/privacy.component';
import { TermsComponent } from './components/public/terms.component';
// src/app/app.module.ts

import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';

import { AppRoutingModule } from "./app-routing.module";
import { SharedModule } from './shared/shared.module';
import { NotificationToastComponent } from './components/shared/notification-toast/notification-toast.component';
import { AppComponent } from "./app.component";

// Componentes
import { HomeComponent } from "./components/home/home.component";
import { HomePublicComponent } from "./components/home-public/home-public.component";
import { HomeStatsComponent } from "./components/home/home-stats/home-stats.component";
import { HomeKnowledgeExpirationsComponent } from "./components/home/home-knowledge-expirations/home-knowledge-expirations.component";
import { HomeKnowledgeEntitiesComponent } from "./components/home/home-knowledge-entities/home-knowledge-entities.component";
import { HomeEntityActionsComponent } from "./components/home/home-entity-actions/home-entity-actions.component";
import { HomeAlertsWidgetComponent } from "./components/home/home-alerts-widget/home-alerts-widget.component";
import { HomeRecentChangesWidgetComponent } from "./components/home/home-recent-changes-widget/home-recent-changes-widget.component";
import { HomeDatesRadarComponent } from "./components/home/home-dates-radar/home-dates-radar.component";
import { HomeLastActivityComponent } from "./components/home/home-last-activity/home-last-activity.component";
import { HomeDecisionPulseStripComponent } from "./components/home/home-decision-pulse-strip/home-decision-pulse-strip.component";
import { HomeQaHarnessComponent } from "./components/home/home-qa-harness/home-qa-harness.component";
import { HomeNextBestDecisionComponent } from "./components/home/home-next-best-decision/home-next-best-decision.component";
import { HomeDecisionSummaryComponent } from "./components/home/home-decision-summary/home-decision-summary.component";
import { HomeDecisionListComponent } from "./components/home/home-decision-list/home-decision-list.component";
import { HomeDecisionCalendarComponent } from "./components/home/home-decision-calendar/home-decision-calendar.component";
import { HomeDecisionCalendarAgendaComponent } from "./components/home/home-decision-calendar/home-decision-calendar-agenda.component";
import { HomeDecisionDetailComponent } from "./components/home/home-decision-detail/home-decision-detail.component";
import { HomeDecisionSupportingSectionsComponent } from "./components/home/home-decision-detail/home-decision-supporting-sections.component";
import { HomeDecisionActionFormComponent } from "./components/home/home-decision-action-form/home-decision-action-form.component";
import { UploadComponent } from "./components/documents/upload/upload.component";
import { AboutComponent } from "./components/about/about.component";
import { HeaderComponent } from "./components/shared/header/header.component";
import { SidebarComponent } from "./components/shared/sidebar/sidebar.component";
import { FooterComponent } from "./components/shared/footer/footer.component";
import { LoginComponent } from "./components/auth/login/login.component";
import { SignupComponent } from "./components/auth/signup/signup.component";
import { WizardComponent } from "./components/auth/wizard/wizard.component";
import { WizardCompletionStepComponent } from "./components/auth/wizard/wizard-completion-step.component";
import { WizardProfileStepsComponent } from "./components/auth/wizard/wizard-profile-steps.component";
import { InviteWizardComponent } from "./components/auth/invite-wizard/invite-wizard.component";
import { ChatComponent } from "./components/chat/chat.component";
import { ChatComposerComponent } from "./components/chat/chat-composer.component";
import { ChatConversationComponent } from "./components/chat/chat-conversation.component";
import { ChatSessionDialogComponent } from "./components/chat/chat-session-dialog.component";
import { ChatSourcesPanelComponent } from "./components/chat/chat-sources-panel.component";
import { SafeBotHtmlPipe } from "./services/chat/presentation/safe-bot-html.pipe";
import { QueryCsvModule } from "./components/query-csv/query-csv.module";
import { ProfileComponent } from "./components/profile/profile.component";
import { GoogleCallbackComponent } from './components/settings/google-callback/google-callback.component';
import { MicrosoftCallbackComponent } from './components/microsoft-callback/microsoft-callback.component';
import { ProfilePhotoDialogComponent } from './components/profile/profile-photo-dialog.component';
import { DocumentOverviewComponent } from './components/documents/overview/document-overview.component';
import { DocumentsTabsComponent } from './components/documents/documents-tabs.component';
import { DocumentNetworkComponent } from './components/documents/network/document-network.component';
import { DocumentDetailsComponent } from 'src/app/components/documents/details/document-details.component';
import { AboutInnovadocIaComponent } from './components/about-innovadoc/about-innovadoc-ia.component';
import { DriveViewerComponent } from './components/documents/drive-viewer/drive-viewer.component';
import { DriveViewerContentComponent } from './components/documents/drive-viewer/drive-viewer-content.component';
import { DriveViewerDialogsComponent } from './components/documents/drive-viewer/drive-viewer-dialogs.component';
import { DriveViewerPreviewComponent } from './components/documents/drive-viewer/drive-viewer-preview.component';
import { DriveViewerTreeComponent } from './components/documents/drive-viewer/drive-viewer-tree.component';

// Servicios
import { ThemeService } from "./services/theme/theme.service";
import { LanguageService } from "./services/language/language.service";
import { NotificationService } from "./services/notification.service";
import { AuthService } from "./services/auth/auth.service";
import { ApiService } from "./services/api.service";
import { ChatService } from "./services/chat/api/chat.service";
import { UserService } from "./services/user.service";

// Guardias
import { AuthGuard } from "./guards/auth.guard";
import { AdminGuard } from "./guards/admin.guard";

// Interceptor de Auth0
import { AuthModule, AuthHttpInterceptor } from "@auth0/auth0-angular";
import { environment } from "../environments/environment";
import { TimeoutInterceptor } from './interceptors/timeout.interceptor';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { LanguageInterceptor } from './interceptors/language.interceptor';
import { CorrelationIdInterceptor } from './interceptors/correlation-id.interceptor';
import { HomeIaRateLimitInterceptor } from './interceptors/home-ia-rate-limit.interceptor';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { DialogUploadQueuedComponent } from './components/shared/dialog-upload-queued/dialog-upload-queued.component';
import { DialogUploadDuplicatedComponent } from './components/shared/dialog-upload-duplicated/dialog-upload-duplicated.component';
import { DialogUploadSuccessComponent } from './components/shared/dialog-upload-success/dialog-upload-success.component';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { NgxEchartsModule } from 'ngx-echarts';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatExpansionModule } from '@angular/material/expansion';
import { BreadcrumbInputComponent } from './components/documents/upload/breadcrumb-input/breadcrumb-input.component';
import { MatMenuModule } from '@angular/material/menu';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { isProviderOAuthCallbackPath } from './services/auth/provider-oauth-callback.util';
import { PlansSharedModule } from './features/plans/plans-shared.module';

@NgModule({
    declarations: [
        AppComponent,
        HomeComponent,
        HomeStatsComponent,
        HomeKnowledgeExpirationsComponent,
        HomeKnowledgeEntitiesComponent,
        HomeEntityActionsComponent,
        HomeAlertsWidgetComponent,
        HomeRecentChangesWidgetComponent,
        HomeDatesRadarComponent,
        HomeLastActivityComponent,
        HomeDecisionPulseStripComponent,
        HomeQaHarnessComponent,
        HomeNextBestDecisionComponent,
        HomeDecisionSummaryComponent,
        HomeDecisionListComponent,
        HomeDecisionCalendarComponent,
        HomeDecisionCalendarAgendaComponent,
        HomeDecisionDetailComponent,
        HomeDecisionSupportingSectionsComponent,
        HomeDecisionActionFormComponent,
        UploadComponent,
        // QueryComponent, // removed
        AboutComponent,
        HeaderComponent,
        SidebarComponent,
        FooterComponent,
        LoginComponent,
        SignupComponent,
        WizardComponent,
        WizardCompletionStepComponent,
        WizardProfileStepsComponent,
        ChatComponent,
        ChatComposerComponent,
        ChatConversationComponent,
        ChatSessionDialogComponent,
        ChatSourcesPanelComponent,
        SafeBotHtmlPipe,
        ProfileComponent,
        GoogleCallbackComponent,
        MicrosoftCallbackComponent,
        DialogUploadQueuedComponent,
        DialogUploadDuplicatedComponent,
        DialogUploadSuccessComponent,
        ProfilePhotoDialogComponent,
        DocumentOverviewComponent,
        DocumentsTabsComponent,
        DocumentNetworkComponent,
        DocumentDetailsComponent,
        AboutInnovadocIaComponent,
        DriveViewerComponent,
        DriveViewerContentComponent,
        DriveViewerDialogsComponent,
        DriveViewerPreviewComponent,
        DriveViewerTreeComponent,
        PrivacyComponent,
        TermsComponent,
        BreadcrumbInputComponent,
    ],
    bootstrap: [AppComponent],
    exports: [
        DialogUploadQueuedComponent,
        DialogUploadDuplicatedComponent,
        DialogUploadSuccessComponent
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA], imports: [BrowserModule,
        AppRoutingModule,
        SharedModule,
        PlansSharedModule,
        NotificationToastComponent,
        FormsModule,
        ReactiveFormsModule,
        QueryCsvModule,
        CommonModule,
        RouterModule,
        AuthModule.forRoot({
            domain: environment.auth0.domain,
            clientId: environment.auth0.clientId,
            skipRedirectCallback: isProviderOAuthCallbackPath(),
            authorizationParams: {
                redirect_uri: globalThis.location?.origin ?? "",
                audience: environment.auth0.authorizationParams.audience,
                scope: environment.auth0.authorizationParams.scope
            },
            cacheLocation: environment.auth0.cacheLocation,
            useRefreshTokens: environment.auth0.useRefreshTokens,
            useRefreshTokensFallback: environment.auth0.useRefreshTokensFallback,
            httpInterceptor: {
                // Intercept calls to our API endpoints, excepto endpoints públicos
                allowedList: [
                  {
                    // Interceptar todas las llamadas a la API EXCEPTO las públicas
                    uri: `${environment.apiUrl}/*`,
                    allowAnonymous: true, // Permitir llamadas sin token (no forzar login)
                  }
                ],
            },
        }),
        BrowserAnimationsModule,
        MatDialogModule,
        MatButtonModule,
        MatProgressSpinnerModule,
        MatTabsModule,
        MatTooltipModule,
        DragDropModule,
        NgxEchartsModule.forRoot({
            echarts: () => import('echarts')
        }),
        MatIconModule,
        MatSnackBarModule,
        MatExpansionModule,
        MatIconModule,
        MatButtonModule,
        MatMenuModule,
        MatFormFieldModule,
        MatInputModule,
        InviteWizardComponent,
        HomePublicComponent,
    ], providers: [
            ThemeService,
            LanguageService,
            NotificationService,
            AuthService,
            ApiService,
            ChatService,
            UserService,
            AuthGuard,
            AdminGuard,
            // Ensure our gating interceptor runs BEFORE the Auth0 interceptor
            // Nuestro interceptor (adjunta token si está disponible). Debe ir ANTES que el de Auth0
            {
                provide: HTTP_INTERCEPTORS,
                useClass: AuthInterceptor,
                multi: true,
            },
            {
                provide: HTTP_INTERCEPTORS,
                useClass: LanguageInterceptor,
                multi: true,
            },
            {
                provide: HTTP_INTERCEPTORS,
                useClass: CorrelationIdInterceptor,
                multi: true,
            },
            {
                provide: HTTP_INTERCEPTORS,
                useClass: HomeIaRateLimitInterceptor,
                multi: true,
            },
            {
                provide: HTTP_INTERCEPTORS,
                useClass: AuthHttpInterceptor,
                multi: true,
            },
            {
                provide: HTTP_INTERCEPTORS,
                useClass: TimeoutInterceptor,
                multi: true
            },
            provideHttpClient(withInterceptorsFromDi())
        ]
})
export class AppModule { }
